import { useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import env from "services/EnvironmentManager";
import { useCreateFileMutation, useLazyGetFileQuery, uploadFile } from "redux/reducers/filesApi";
import { PROVIDERS, PROXY_ENABLED, PROXY_EXTERNAL_BUCKETS, PROXY_INTERNAL_BUCKETS } from "utilities/constants";
import { getBucketName, ENV_FILE_PREFIX } from "@ubiops/widgets";
import { createErrorNotification } from "utilities/notifications";
import { ERROR, LOADED, LOADING } from "utilities/request-statuses";
import { PROXY_URL } from "services/request";

const useCreateFile = (token, request) => {
  const { projectName } = useParams();
  const [createFileMutation] = useCreateFileMutation();
  const [getFile] = useLazyGetFileQuery();
  const [createFileStatus, setCreateFileStatus] = useState({});
  const [currentFiles, setCurrentFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});

  const dispatch = useDispatch();

  const createFile = async (_, formData, { fileName, fieldName: inputName }) => {
    const file = formData.get('file');
    const bucketName = getBucketName(inputName, request);

    setCreateFileStatus(prev => ({ ...prev, [fileName]: LOADING }));

    const response = await createFileMutation({ projectName, fileName: file?.name, body: {}, token, inputName, bucketName });
    if (response.error) {
      const errorMessage = response.error.data?.error || "Failed to upload file. You may not have permission.";
      dispatch(createErrorNotification(errorMessage));
      setCreateFileStatus(prev => ({ ...prev, [fileName]: ERROR }));
      return;
    }

    const { url, provider } = response.data;

    const proxyEnabled = env.get(PROXY_ENABLED);
    const proxyExternalBuckets = env.get(PROXY_EXTERNAL_BUCKETS);
    const proxyInternalBuckets = env.get(PROXY_INTERNAL_BUCKETS);
    const useProxy =
      (provider === PROVIDERS.ubiops && proxyInternalBuckets) ||
      (provider !== PROVIDERS.ubiops && proxyExternalBuckets);

    const uploadUrl = proxyEnabled && useProxy
      ? `https://${PROXY_URL}/${url.replace(/^https?:\/\//, "")}`
      : url;

    try {
      await uploadFile(uploadUrl, file, provider, (percentage) =>
        setUploadProgress(prev => ({ ...prev, [fileName]: percentage }))
      );
      setCurrentFiles(prev => ({ ...prev, [fileName]: `${ENV_FILE_PREFIX}${bucketName}/${file.name}` }));
      setCreateFileStatus(prev => ({ ...prev, [fileName]: LOADED }));
    } catch (err) {
      dispatch(createErrorNotification(err.message));
      setCreateFileStatus(prev => ({ ...prev, [fileName]: ERROR }));
    }
  };

  const checkFileExists = async (fileName, fieldName) => {
    const bucketName = getBucketName(fieldName, request);
    try {
      return await getFile({ projectName, bucketName, fileName, token }).unwrap();
    } catch (err) {
      if (err?.status !== 404) {
        console.error("Failed to check if file exists", err);
      }
      return null;
    }
  };

  return { createFile, currentFiles, createFileStatus, uploadProgress, checkFileExists };
};

export default useCreateFile;
